/**
 * features/finance/FeeArrangements.tsx
 * ------------------------------------------------------------------
 * SPECIAL FEE ARRANGEMENTS — payment plans for families in arrears.
 *
 * Search a family, see what they owe, agree a number of instalments, and
 * from then on the screen watches whether the money actually arrives.
 *
 * It takes no payments and raises no invoices. Receipts are captured in
 * Payments exactly as before; this reads them. That separation is the
 * whole design: a second place to take money would give a family two
 * balances that could disagree, and the one on the arrangement would be
 * the one nobody reconciles.
 *
 * "Paid to date" is therefore always the family's REAL receipts since the
 * plan began — so a payment captured this morning shows here without
 * anyone touching this screen, and a voided receipt corrects itself.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { Search, AlertTriangle, Check, Plus, X, Handshake } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { doc } from 'firebase/firestore';
import { Button, Spinner } from '@/components/ui/primitives';
import { FormModal, modalPrimaryBtn, modalGhostBtn } from '@/components/ui/glass';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { getPermissions } from '@/lib/permissions';
import { fmtPula, round2 } from '@/lib/financeCore';
import { PageHeader, SoftCard, StatChip, glassFieldClass, ExportBar } from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';
import {
  ARRANGEMENT_COLLECTION, buildSchedule, assessArrangement, daysBehind, STATE_LABEL,
  type FeeArrangement, type ArrangementFrequency, type ArrangementProgress,
} from '@/lib/feeArrangements';

const today = () => new Date().toISOString().slice(0, 10);

const STATE_TONE: Record<ArrangementProgress['state'], string> = {
  on_track: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  ahead: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200',
  behind: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
  settled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  not_started: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

export default function FeeArrangements() {
  const acad = useData();
  const data = useFinanceData();
  const { userData, userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const mayManage = perms.canManageFinanceConfig;

  const [rows, setRows] = useState<FeeArrangement[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, ARRANGEMENT_COLLECTION), (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FeeArrangement));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  /** Outstanding per family, from the real invoice ledger. */
  const balanceByFamily = useMemo(() => {
    const m = new Map<string, number>();
    data.invoices.forEach((i) => {
      if (i.status === 'void' || !i.familyId) return;
      m.set(i.familyId, round2((m.get(i.familyId) ?? 0) + (i.balance || 0)));
    });
    return m;
  }, [data.invoices]);

  /** Real receipts per family, per date — the basis of "paid to date". */
  const paymentsByFamily = useMemo(() => {
    const m = new Map<string, { date: string; amount: number; receiptNo?: string; studentName?: string }[]>();
    data.payments.forEach((p) => {
      if (!p.familyId) return;
      const list = m.get(p.familyId) ?? [];
      list.push({
        date: String(p.receivedAt || p.createdAt || '').slice(0, 10),
        amount: Number(p.amount) || 0,
        receiptNo: p.receiptNo,
        studentName: p.studentName,
      });
      m.set(p.familyId, list);
    });
    m.forEach((list) => list.sort((a, b) => b.date.localeCompare(a.date)));
    return m;
  }, [data.payments]);

  const paidSince = (familyId: string, startDate: string) =>
    round2((paymentsByFamily.get(familyId) ?? [])
      .filter((p) => p.date >= startDate)
      .reduce((t, p) => t + p.amount, 0));

  const assessed = useMemo(
    () => rows
      .map((a) => ({ a, p: assessArrangement(a, paidSince(a.familyId, a.startDate)) }))
      // Trouble first — this screen exists to surface who has stopped paying.
      .sort((x, y) => {
        const order = { behind: 0, not_started: 1, on_track: 2, ahead: 3, settled: 4 };
        return order[x.p.state] - order[y.p.state] || y.p.variance - x.p.variance;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, paymentsByFamily],
  );

  const behind = assessed.filter((x) => x.p.state === 'behind');
  const active = assessed.filter((x) => x.a.status === 'active');
  const opened = openId ? assessed.find((x) => x.a.id === openId) ?? null : null;

  async function save(a: FeeArrangement) {
    await setDoc(doc(db, ARRANGEMENT_COLLECTION, a.id), a, { merge: true });
  }

  async function close(a: FeeArrangement, status: FeeArrangement['status'], reason: string) {
    await save({ ...a, status, closedAt: new Date().toISOString(), closedReason: reason, updatedAt: new Date().toISOString() });
    setMsg({ ok: true, text: `${a.familyName} marked ${status}.` });
    setOpenId(null);
  }

  const exportDoc = useMemo((): TableExport<(typeof assessed)[number]> => ({
    title: 'Payment Plans',
    meta: `${assessed.length} arrangement${assessed.length === 1 ? '' : 's'}`,
    filename: 'payment-plans',
    orientation: 'l',
    columns: [
      { header: 'Family', text: (x) => x.a.familyName ?? x.a.familyId },
      { header: 'Agreed', width: 24, text: (x) => x.a.startDate ?? '' },
      { header: 'Instalment (P)', align: 'right', width: 30, text: (x) => fmtPula(x.a.instalmentAmount), raw: (x) => x.a.instalmentAmount },
      { header: 'Every', width: 24, text: (x) => x.a.frequency },
      { header: 'Instalments', align: 'right', width: 24, text: (x) => `${x.p.instalmentsCovered} of ${x.a.instalmentCount}`, raw: (x) => x.p.instalmentsCovered },
      { header: 'Expected (P)', align: 'right', width: 28, text: (x) => fmtPula(x.p.expectedToDate), raw: (x) => x.p.expectedToDate },
      { header: 'Paid since (P)', align: 'right', width: 30, text: (x) => fmtPula(x.p.paidToDate), raw: (x) => x.p.paidToDate },
      { header: 'Variance (P)', align: 'right', width: 28, text: (x) => fmtPula(x.p.variance), raw: (x) => x.p.variance },
      { header: 'State', width: 26, text: (x) => x.p.state.replace('_', ' ') },
    ],
    rows: assessed,
    note: 'Measured from what the family has actually paid SINCE the arrangement was agreed, '
      + 'not from their overall balance — a plan is a promise about future payments, and '
      + 'judging it on an old debt would mark every family behind on the day they signed.',
  }), [assessed]);

  if (loading || data.loading) return <Spinner label="Loading arrangements..." />;

  return (
    <div className="space-y-4">
      <PageHeader icon={Handshake}
        title="Special Fee Arrangements"
        subtitle="Payment plans for families settling arrears — payments are still captured in Payments"
        actions={(
          <>
            <ExportBar onPdf={() => downloadTablePdf(exportDoc)} onCsv={() => downloadCsv(exportDoc)} disabled={!assessed.length} />
            {mayManage && <Button icon={<Plus size={16} />} onClick={() => { setAdding(true); setMsg(null); }}>New arrangement</Button>}
          </>
        )}
      />

      {msg && (
        <SoftCard pad="p-3">
          <p className={`text-sm flex items-start gap-2 ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600'}`}>
            {msg.ok ? <Check size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
            {msg.text}
          </p>
        </SoftCard>
      )}

      <div className="flex flex-wrap gap-3">
        <StatChip label="Active plans" value={String(active.length)} />
        <StatChip label="Behind plan" value={String(behind.length)} tone={behind.length ? 'red' : undefined} />
        <StatChip label="Under arrangement" value={fmtPula(active.reduce((s, x) => s + x.a.openingBalance, 0))} />
        <StatChip label="Still to settle" value={fmtPula(active.reduce((s, x) => s + x.p.remaining, 0))} />
      </div>

      {behind.length > 0 && (
        <SoftCard pad="p-3">
          <p className="text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              <strong>{behind.length} famil{behind.length === 1 ? 'y has' : 'ies have'} fallen behind.</strong>{' '}
              {behind.slice(0, 4).map((x) => x.a.familyName).join(', ')}
              {behind.length > 4 ? ` and ${behind.length - 4} more` : ''}.
            </span>
          </p>
        </SoftCard>
      )}

      {assessed.length === 0 ? (
        <SoftCard pad="p-8">
          <p className="text-sm text-gray-500 text-center">
            No payment plans yet. Use <strong>New arrangement</strong> to put a family in arrears onto instalments.
          </p>
        </SoftCard>
      ) : (
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[48rem]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left font-semibold py-2 px-3">Family</th>
                  <th className="text-left font-semibold py-2 w-28">Status</th>
                  <th className="text-right font-semibold py-2 w-28">Plan</th>
                  <th className="text-right font-semibold py-2 w-28">Paid</th>
                  <th className="text-right font-semibold py-2 w-28">Remaining</th>
                  <th className="text-right font-semibold py-2 w-28">Variance</th>
                  <th className="text-left font-semibold py-2 px-3 w-32">Next due</th>
                </tr>
              </thead>
              <tbody>
                {assessed.map(({ a, p }) => (
                  <tr
                    key={a.id}
                    onClick={() => setOpenId(a.id)}
                    className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <td className="py-2 px-3">
                      <span className="font-medium text-gray-800 dark:text-gray-100">{a.familyName}</span>
                      <span className="block text-xs text-gray-400">
                        {a.instalmentCount} × {fmtPula(a.instalmentAmount)} {a.frequency}
                      </span>
                    </td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATE_TONE[p.state]}`}>{STATE_LABEL[p.state]}</span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmtPula(a.openingBalance)}</td>
                    <td className="py-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{fmtPula(p.paidToDate)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtPula(p.remaining)}</td>
                    <td className={`py-2 text-right tabular-nums font-semibold ${p.variance < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {p.variance === 0 ? '—' : fmtPula(p.variance)}
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500">
                      {p.state === 'settled' ? '—' : p.nextDue
                        ? `${p.nextDue.dueDate}${p.instalmentsBehind ? ` · ${p.instalmentsBehind} missed` : ''}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}

      {adding && (
        <NewArrangement
          families={acad.families.map((f) => ({ id: f.id, name: f.name }))}
          balanceByFamily={balanceByFamily}
          existing={rows}
          onClose={() => setAdding(false)}
          onSave={async (a) => {
            await save(a);
            setAdding(false);
            setMsg({ ok: true, text: `Plan agreed for ${a.familyName}: ${a.instalmentCount} × ${fmtPula(a.instalmentAmount)}.` });
          }}
          by={{ uid: userData?.uid ?? '', name: userData?.displayName ?? 'Unknown' }}
        />
      )}

      {opened && (
        <ArrangementDetail
          a={opened.a}
          p={opened.p}
          payments={(paymentsByFamily.get(opened.a.familyId) ?? []).filter((x) => x.date >= opened.a.startDate)}
          mayManage={mayManage}
          onClose={() => setOpenId(null)}
          onCloseOut={close}
        />
      )}
    </div>
  );
}

/* ============================ new arrangement ============================ */

function NewArrangement({
  families, balanceByFamily, existing, onClose, onSave, by,
}: {
  families: { id: string; name: string }[];
  balanceByFamily: Map<string, number>;
  existing: FeeArrangement[];
  onClose: () => void;
  onSave: (a: FeeArrangement) => Promise<void>;
  by: { uid: string; name: string };
}) {
  const [q, setQ] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [count, setCount] = useState(6);
  const [frequency, setFrequency] = useState<ArrangementFrequency>('monthly');
  const [startDate, setStartDate] = useState(today());
  const [note, setNote] = useState('');
  const [agreedWith, setAgreedWith] = useState('');
  const [busy, setBusy] = useState(false);

  /** Only families that actually owe something — a plan for a family in
   *  credit is a data-entry mistake, so they are not offered. */
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    return families
      .filter((f) => (balanceByFamily.get(f.id) ?? 0) > 0)
      .filter((f) => !term || f.name.toLowerCase().includes(term))
      .sort((a, b) => (balanceByFamily.get(b.id) ?? 0) - (balanceByFamily.get(a.id) ?? 0))
      .slice(0, 30);
  }, [families, balanceByFamily, q]);

  const chosen = families.find((f) => f.id === familyId);
  const balance = familyId ? balanceByFamily.get(familyId) ?? 0 : 0;
  const alreadyOn = existing.find((e) => e.familyId === familyId && e.status === 'active');
  const schedule = useMemo(
    () => buildSchedule(balance, count, startDate, frequency),
    [balance, count, startDate, frequency],
  );

  async function submit() {
    if (!chosen || schedule.length === 0) return;
    setBusy(true);
    const now = new Date().toISOString();
    await onSave({
      id: `arr_${familyId}_${startDate}`,
      familyId, familyName: chosen.name,
      openingBalance: round2(balance),
      startDate, frequency,
      instalmentCount: count,
      instalmentAmount: schedule[schedule.length - 1].amount,
      schedule,
      status: 'active',
      note: note.trim() || undefined,
      agreedWith: agreedWith.trim() || undefined,
      createdBy: by.uid, createdByName: by.name,
      createdAt: now, updatedAt: now,
    });
    setBusy(false);
  }

  return (
    <FormModal
      title="New payment plan"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={modalGhostBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={modalPrimaryBtn} disabled={busy || !chosen || !!alreadyOn || schedule.length === 0} onClick={submit}>
            {busy ? 'Saving…' : 'Agree plan'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {!chosen ? (
          <>
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Find the family</label>
              <div className="relative mt-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input className={glassFieldClass('pl-9')} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Family name" />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Only families with an outstanding balance are listed.</p>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {matches.map((f) => (
                <button key={f.id} type="button" onClick={() => setFamilyId(f.id)}
                  className="w-full flex items-center justify-between gap-3 py-2 px-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded">
                  <span className="text-gray-800 dark:text-gray-100">{f.name}</span>
                  <span className="tabular-nums font-semibold text-rose-600">{fmtPula(balanceByFamily.get(f.id) ?? 0)}</span>
                </button>
              ))}
              {matches.length === 0 && <p className="text-sm text-gray-500 py-3">No family in arrears matches that.</p>}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{chosen.name}</p>
                <p className="text-xs text-gray-500">Outstanding today · {fmtPula(balance)}</p>
              </div>
              <button type="button" onClick={() => setFamilyId('')} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>

            {alreadyOn && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This family already has an active plan agreed {alreadyOn.startDate}. Close it before starting another.
              </p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Instalments</label>
                <input type="number" min={1} max={36} className={glassFieldClass('mt-1')} value={count}
                  onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Frequency</label>
                <select className={glassFieldClass('mt-1')} value={frequency}
                  onChange={(e) => setFrequency(e.target.value as ArrangementFrequency)}>
                  <option value="monthly">Monthly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="termly">Termly</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">First payment</label>
                <input type="date" className={glassFieldClass('mt-1')} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Agreed with</label>
              <input className={glassFieldClass('mt-1')} value={agreedWith} onChange={(e) => setAgreedWith(e.target.value)}
                placeholder="Parent or guardian's name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Note</label>
              <input className={glassFieldClass('mt-1')} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Why the arrangement was made — reviewed each term" />
            </div>

            {schedule.length > 0 && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800">
                  {count} payments of about {fmtPula(schedule[schedule.length - 1].amount)} · finishing {schedule[schedule.length - 1].dueDate}
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                  {schedule.map((s, i) => (
                    <div key={s.dueDate} className="flex justify-between px-3 py-1.5 text-sm">
                      <span className="text-gray-500">{i + 1}. {s.dueDate}</span>
                      <span className="tabular-nums">{fmtPula(s.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </FormModal>
  );
}

/* ============================== detail ============================== */

function ArrangementDetail({
  a, p, payments, mayManage, onClose, onCloseOut,
}: {
  a: FeeArrangement;
  p: ArrangementProgress;
  payments: { date: string; amount: number; receiptNo?: string; studentName?: string }[];
  mayManage: boolean;
  onClose: () => void;
  onCloseOut: (a: FeeArrangement, status: FeeArrangement['status'], reason: string) => Promise<void>;
}) {
  const late = daysBehind(p);
  return (
    <FormModal
      title={`${a.familyName} — payment plan`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={modalGhostBtn} onClick={onClose}>Close</button>
          {mayManage && a.status === 'active' && (
            <>
              <button type="button" className={modalGhostBtn}
                onClick={() => onCloseOut(a, 'defaulted', 'Marked defaulted from the arrangements screen')}>
                Mark defaulted
              </button>
              <button type="button" className={modalPrimaryBtn}
                onClick={() => onCloseOut(a, 'completed', 'Settled in full')}>
                Mark settled
              </button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatChip label="Agreed" value={fmtPula(a.openingBalance)} />
          <StatChip label="Paid since start" value={fmtPula(p.paidToDate)} tone="emerald" />
          <StatChip label="Remaining" value={fmtPula(p.remaining)} />
          <StatChip label="Variance" value={fmtPula(p.variance)} tone={p.variance < 0 ? 'red' : 'emerald'} />
        </div>

        <div className={`rounded-lg px-3 py-2 text-sm ${STATE_TONE[p.state]}`}>
          <strong>{STATE_LABEL[p.state]}</strong>
          {p.state === 'behind' && (
            <> — {p.instalmentsBehind} instalment{p.instalmentsBehind === 1 ? '' : 's'} missed
              {late > 0 ? `, oldest ${late} day${late === 1 ? '' : 's'} overdue` : ''}.
              Expected {fmtPula(p.expectedToDate)} by today, received {fmtPula(p.paidToDate)}.
            </>
          )}
          {p.state === 'on_track' && <> — {p.instalmentsCovered} of {a.instalmentCount} instalments covered.</>}
          {p.state === 'settled' && <> — the agreed amount has been paid in full.</>}
        </div>

        {a.agreedWith && <p className="text-xs text-gray-500">Agreed with {a.agreedWith} on {a.startDate}.</p>}
        {a.note && <p className="text-xs text-gray-500 italic">{a.note}</p>}

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Schedule</h4>
          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            {a.schedule.map((s, i) => (
              <div key={s.dueDate} className="flex justify-between items-center px-3 py-1.5 text-sm">
                <span className="text-gray-500">{i + 1}. {s.dueDate}</span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{fmtPula(s.amount)}</span>
                  {i < p.instalmentsCovered
                    ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">covered</span>
                    : s.dueDate <= today()
                      ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">missed</span>
                      : <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">upcoming</span>}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">
            Payments received since {a.startDate}
          </h4>
          {payments.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing received yet.</p>
          ) : (
            <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              {payments.map((x, i) => (
                <div key={`${x.receiptNo}-${i}`} className="flex justify-between px-3 py-1.5 text-sm">
                  <span className="text-gray-500">{x.date} · {x.receiptNo ?? 'receipt'}{x.studentName ? ` · ${x.studentName}` : ''}</span>
                  <span className="tabular-nums text-emerald-700 dark:text-emerald-400">{fmtPula(x.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-1">
            Captured in Payments as normal — this screen only reads them.
          </p>
        </div>
      </div>
    </FormModal>
  );
}
